import React, { useEffect, useCallback } from "react"
import { Link, useHistory, useLocation } from "react-router-dom"
import { ContentfulConditionalGate } from "../../lib/contentful/components/ContentfulConditionGate"
import { ReadAndLearnBreadcrumbs } from "../../components/read/shared/ReadAndLearnBreadcrumbs"
import slugify from "slugify"
import { CarrotPlanRequiredModal } from "../../components/views/app/CarrotPlanRequiredModal"
import { GuideComponentProps } from "./GuideParser"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { getHeap } from "./utils/heap"
import { FormattedMessage, useIntl } from "react-intl"
import { ContentfulRawMarkdownRenderer } from "#/services/contentful"
import {
  Box,
  Button,
  CloseIcon,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tile,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { Title } from "#/services/page-title/Title"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageBreadcrumbHeader } from "#/features/global-ui/page-header"
import { CarrotAcademySearch } from "#/components/read/CarrotAcademySearch"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { FavoriteButton } from "#/components/read/FavoriteButton"
import { FeedbackContainer } from "#/components/read/FeedbackContainer"
const fixMarkdownHeaders = { "*:first-child": { marginTop: 0 } }

export type LandingPageState = {
  isPostGuide?: boolean
  postGuideDialogClosed?: boolean
}

const Banner = ({
  title,
  guideId,
  categoryId
}: {
  title: string
  guideId: string
  categoryId: string
}): JSX.Element => {
  return (
    <Tile variant="solid" color="primary" sx={{ inlineSize: "100%" }}>
      <Stack direction="column" sx={{ marginInlineStart: { lg: "32px" } }}>
        <Typography variant="overline" color={(theme) => theme.palette.text.tertiary} sx={{ zIndex: 1 }}>
          <FormattedMessage defaultMessage="Guide" />
        </Typography>
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          gap={(theme) => theme.spacing(theme.tokens.spacing.sm)}
        >
          <Box sx={{ zIndex: 1, paddingInlineEnd: { md: "160px", lg: "50px" } }}>
            <Typography variant="h1">{title}</Typography>
          </Box>
          <Box>
            <FavoriteButton contentfulEntryId={guideId} categoryEntryId={categoryId} contentType="guide" />
          </Box>
        </Stack>
      </Stack>
    </Tile>
  )
}

const Description = ({ descriptionMarkdown }: { descriptionMarkdown: string }): JSX.Element => {
  return (
    <Box
      sx={(theme) => ({
        inlineSize: { xs: "100%", lg: "calc(70% - 24px)" },
        maxInlineSize: "600px",
        paddingInline: theme.spacing(theme.tokens.spacing.md)
      })}
    >
      <ContentfulRawMarkdownRenderer>{descriptionMarkdown}</ContentfulRawMarkdownRenderer>
    </Box>
  )
}

const Audience = ({ audienceMarkdown }: { audienceMarkdown: string }): JSX.Element => {
  return (
    <Tile
      variant="secondary"
      color="blue"
      sx={{
        flex: { lg: 1 },
        ...fixMarkdownHeaders
      }}
    >
      <ContentfulRawMarkdownRenderer>{audienceMarkdown}</ContentfulRawMarkdownRenderer>
    </Tile>
  )
}

const Overview = ({ overviewMarkdown }: { overviewMarkdown: string }): JSX.Element => {
  return (
    <Tile
      variant="secondary"
      color="green"
      sx={{
        flex: { lg: 2 },
        ...fixMarkdownHeaders
      }}
    >
      <ContentfulRawMarkdownRenderer>{overviewMarkdown}</ContentfulRawMarkdownRenderer>
    </Tile>
  )
}

const LegalFooter = (): JSX.Element => {
  const theme = useTheme()
  return (
    <Stack direction="column" gap={(theme) => theme.spacing(theme.tokens.spacing.md)} sx={{ marginBlockEnd: "90px" }}>
      <Divider />
      <Typography variant="body2" color={theme.palette.text.tertiary}>
        <FormattedMessage
          defaultMessage="Legality and availability of services vary from country to country and often even within a country. Contact your
      Care Team for help understanding what services are available to you domestically or through cross-border care."
          description="Legal disclaimer for articles and videos. Care Team refers to the care navigators that help Carrot members navigate their fertility journey"
        />
      </Typography>
      <Divider sx={{ inlineSize: "47px" }} />
      <Typography variant="body2" color={theme.palette.text.tertiary}>
        <FormattedMessage
          defaultMessage="We strive to be as clear and inclusive as possible. When appropriate, we use terminology that reflects current
      medical practice. We recognize that these terms do not capture every individual's experience."
        />
      </Typography>
      <Divider sx={{ inlineSize: "47px" }} />
      <Typography variant="body2" color={theme.palette.text.tertiary}>
        <FormattedMessage defaultMessage="Carrot resources are for informational purposes only and do not constitute medical advice or professional services. They are not a substitute for legal, tax, licensed clinical care, or professional medical advice, diagnosis, or treatment. If you are experiencing a medical emergency, call emergency services immediately." />
      </Typography>
    </Stack>
  )
}

const LandingPage = ({ guide, subcategory, category }: GuideComponentProps): JSX.Element => {
  const { isInAppLockdown } = useCurrentUser()
  const { state, pathname } = useLocation<LandingPageState>()
  const history = useHistory()
  const intl = useIntl()
  const categorySlug = slugify(category?.fields?.navTitle)
  const [firstSection] = guide.sections
  const guideSectionUrl = `/read/${categorySlug}/guide/${guide.slug}/${firstSection.slug}`
  const guideSlugId = `${guide.slug}-${guide.id}`
  const { mutate: addMemberAction } = useMemberActionTracking()

  const handleAddMemberAction = useCallback(
    (memberActionTaken: MemberActions): void => {
      addMemberAction({
        memberActionTaken,
        metaData: {
          category: category?.fields?.navTitle,
          url: memberActionTaken === MemberActions.CARROT_ACADEMY_GUIDE_OPENED ? guideSectionUrl : pathname,
          entryId: guide.id,
          isCarrotPlanRequiredModalShown: isInAppLockdown
        }
      })
    },
    [addMemberAction, category?.fields?.navTitle, guideSectionUrl, pathname, guide.id, isInAppLockdown]
  )

  useEffect(() => {
    if (isInAppLockdown) {
      getHeap().track("Carrot Plan Required Modal Rendered", {
        "Guide Slug ID": guideSlugId
      })
    }
    if (state?.isPostGuide) {
      getHeap().track("Post Guide Modal Rendered", {
        "Guide Slug ID": guideSlugId
      })
      handleAddMemberAction(MemberActions.CARROT_ACADEMY_GUIDE_COMPLETED)
    }
    if (!state?.isPostGuide && !state?.postGuideDialogClosed) {
      handleAddMemberAction(MemberActions.CARROT_ACADEMY_GUIDE_LANDING_PAGE_VIEWED)
    }
  }, [guideSlugId, handleAddMemberAction, isInAppLockdown, state?.isPostGuide, state?.postGuideDialogClosed])

  return (
    <>
      <Title title={guide.title} productTitle="Carrot Academy" />
      <ContentfulConditionalGate conditions={guide.conditions} allowConditionsOverride={true}>
        <PageLayout
          header={
            <PageBreadcrumbHeader
              breadcrumbs={<ReadAndLearnBreadcrumbs category={category} subcategory={subcategory} />}
              search={<CarrotAcademySearch />}
            />
          }
        >
          <Stack gap={(theme) => theme.spacing(theme.tokens.spacing.xxxl)} direction="column">
            <Stack
              gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
              direction="column"
              data-guide-id={guideSlugId}
            >
              <Banner title={guide.title} guideId={guide.id} categoryId={category.sys.id} />
              <Description descriptionMarkdown={guide.description} />
              <Box sx={(theme) => ({ paddingInline: theme.spacing(theme.tokens.spacing.md) })}>
                <Link to={guideSectionUrl}>
                  <Button
                    onClick={() => handleAddMemberAction(MemberActions.CARROT_ACADEMY_GUIDE_OPENED)}
                    id="get-started-button"
                  >
                    <FormattedMessage defaultMessage="Get started" />
                  </Button>
                </Link>
              </Box>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                gap={(theme) => theme.spacing(theme.tokens.spacing.xxl)}
                sx={{ inlineSize: "100%", alignItems: "stretch" }}
              >
                <Overview overviewMarkdown={guide.overview} />
                <Audience audienceMarkdown={guide.audience} />
              </Stack>
              <FeedbackContainer contentfulEntryId={guide.id} categoryEntryId={category.sys.id} contentType="guide" />
              <LegalFooter />
            </Stack>
          </Stack>
        </PageLayout>
        <Dialog
          aria-labelledby="post-guide-modal-title"
          aria-describedby="post-guide-modal-description"
          open={state?.isPostGuide}
          id="post-guide-modal-container"
          data-guide-id={guideSlugId}
        >
          <Box display="flex" justifyContent="flex-end">
            <IconButton
              aria-label={intl.formatMessage({ defaultMessage: "Close modal" })}
              onClick={() => {
                history.push(pathname, { isPostGuide: false, postGuideDialogClosed: true })
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ gap: 0 }}>
            <ContentfulRawMarkdownRenderer>{guide.afterGuideModal}</ContentfulRawMarkdownRenderer>
          </DialogContent>
        </Dialog>
        <CarrotPlanRequiredModal />
      </ContentfulConditionalGate>
    </>
  )
}

export default LandingPage
